/**
 * Selling Plan Toggle
 * -------------------
 * Powers the One-time / Subscribe & Save widget inside the quick-add popup.
 *
 * What it handles:
 *   - Toggling visibility of the plan name + frequency dropdown.
 *   - Enabling/disabling the `selling_plan` form field so it only submits
 *     when the customer actually picked a subscription.
 *   - Updating the prices on both options when the customer switches
 *     product variant (Vincent updates the hidden `name="id"` input
 *     after a click on `variant-wrapper li`, but does NOT re-render
 *     the popup HTML).
 *   - Updating the plan name + subscription price when the customer
 *     changes the frequency dropdown.
 *
 * Markup contract — see snippets/sections/product-variant-details.liquid.
 *   Required: a <fieldset data-selling-plan-options> containing
 *     - input[name="purchase_type"][value="onetime"|"subscribe"]
 *     - [data-price-onetime], [data-price-subscribe]
 *     - <script type="application/json" data-selling-plan-prices> JSON map
 *     - either <select name="selling_plan"> or <input data-selling-plan-single>
 *
 * Why event delegation on document:
 *   Vincent injects the quick-add popup via `innerHTML`, which does NOT
 *   execute inline <script> tags. A delegated listener bound once on
 *   document keeps working no matter how/when the popup is rendered.
 */
(function () {
  'use strict';

  function getPriceMap(fieldset) {
    var script = fieldset.querySelector('script[data-selling-plan-prices]');
    if (!script) return null;
    if (fieldset.__priceMapCache) return fieldset.__priceMapCache;
    try {
      fieldset.__priceMapCache = JSON.parse(script.textContent);
      return fieldset.__priceMapCache;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[selling-plan-toggle] failed to parse price map', err);
      return null;
    }
  }

  function getCurrentVariantId(fieldset) {
    var form = fieldset.closest('form');
    if (!form) form = document;
    var input = form.querySelector('input[type="hidden"][name="id"]');
    return input ? String(input.value) : null;
  }

  function getCurrentPlanId(fieldset) {
    var select = fieldset.querySelector('select[name="selling_plan"]');
    if (select) return String(select.value);
    var hidden = fieldset.querySelector('input[type="hidden"][data-selling-plan-single]');
    if (hidden) return String(hidden.value);
    return null;
  }

  function priceHTML(price, compareAt) {
    if (compareAt) {
      return (
        '<span class="selling-plan-option__compare-at-price">' +
        compareAt +
        '</span>' +
        price
      );
    }
    return price;
  }

  function updatePrices(fieldset) {
    var map = getPriceMap(fieldset);
    if (!map) return;

    var variantId = getCurrentVariantId(fieldset);
    if (!variantId || !map[variantId]) return;
    var entry = map[variantId];

    var oneTimeEl = fieldset.querySelector('[data-price-onetime]');
    if (oneTimeEl) oneTimeEl.innerHTML = priceHTML(entry.price, entry.compare_at_price);

    var planId = getCurrentPlanId(fieldset);
    var subEl = fieldset.querySelector('[data-price-subscribe]');
    if (subEl) {
      var planPrice = entry.plans && planId && entry.plans[planId];
      if (planPrice) {
        subEl.innerHTML = priceHTML(planPrice.price, planPrice.compare_at_price);
      } else {
        // Fallback: variant doesn't have an allocation for this plan
        subEl.innerHTML = priceHTML(entry.price, entry.compare_at_price);
      }
    }
  }

  function syncCheckedClass(fieldset) {
    var radios = fieldset.querySelectorAll('input[name="purchase_type"]');
    radios.forEach(function (r) {
      var label = r.closest('label');
      if (label) label.classList.toggle('is-checked', r.checked);
    });
  }

  function syncFieldset(fieldset) {
    if (!fieldset) return;

    var checked = fieldset.querySelector('input[name="purchase_type"]:checked');
    var freq = fieldset.querySelector('[data-selling-plan-frequency]');
    var select = fieldset.querySelector('select[name="selling_plan"]');
    var hiddenSingle = fieldset.querySelector('input[type="hidden"][data-selling-plan-single]');
    var nameEl = fieldset.querySelector('[data-selling-plan-name]');
    var isSub = checked && checked.value === 'subscribe';

    if (freq) freq.hidden = !isSub;
    if (select) select.disabled = !isSub;
    if (hiddenSingle) hiddenSingle.disabled = !isSub;

    if (nameEl) {
      nameEl.hidden = !isSub;
      if (isSub && select) {
        var opt = select.options[select.selectedIndex];
        if (opt) nameEl.textContent = opt.textContent;
      }
      // Single-plan case: server already rendered the plan name into nameEl,
      // so we just toggle visibility.
    }

    syncCheckedClass(fieldset);
    updatePrices(fieldset);
  }

  // Toggle / dropdown changes
  document.addEventListener('change', function (e) {
    var target = e.target;
    if (!target || !target.matches) return;

    if (target.matches('input[name="purchase_type"]')) {
      syncFieldset(target.closest('[data-selling-plan-options]'));
    } else if (target.matches('select[name="selling_plan"]')) {
      syncFieldset(target.closest('[data-selling-plan-options]'));
    }
  });

  // Variant switch — Vincent fires no custom event; clicking a variant
  // updates the hidden id input on the next tick. We wait one frame and
  // refresh prices on every selling-plan widget on the page.
  document.addEventListener('click', function (e) {
    var li = e.target.closest && e.target.closest('variant-wrapper li[data-option]');
    if (!li) return;
    requestAnimationFrame(function () {
      setTimeout(function () {
        document.querySelectorAll('[data-selling-plan-options]').forEach(updatePrices);
      }, 30);
    });
  });

  // Initial sync for any fieldsets already in DOM, plus future ones added
  // via innerHTML (Vincent quick-add popup).
  if ('MutationObserver' in window) {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          var fields = node.matches && node.matches('[data-selling-plan-options]')
            ? [node]
            : (node.querySelectorAll
              ? node.querySelectorAll('[data-selling-plan-options]')
              : []);
          for (var k = 0; k < fields.length; k++) syncFieldset(fields[k]);
        }
      }
    });

    function bootstrap() {
      observer.observe(document.body, { childList: true, subtree: true });
      document.querySelectorAll('[data-selling-plan-options]').forEach(syncFieldset);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootstrap);
    } else {
      bootstrap();
    }
  }
})();
